import { DatasourceTab } from '@/modules/collections/types';
import { DatasourceType } from '@/shared/types/collections';
import type { SortDirection } from '@/shared/types/common';

/**
 * Serializes datasource types into a consistent string format for query keys
 * @param types - Array of datasource types
 * @returns Serialized string or 'all' if empty/undefined
 * @example
 * serializeDatasourceTypes([DatasourceType.FILES, DatasourceType.WEBSITE]) // 'files|website'
 * serializeDatasourceTypes([]) // 'all'
 * serializeDatasourceTypes(undefined) // 'all'
 */
export function serializeDatasourceTypes(types?: DatasourceType[]): string {
  return types?.length ? types.slice().sort().join('|') : 'all';
}

/**
 * Query key factories for different hook types
 * These factories ensure consistent query key generation across all hooks
 */
export const queryKeyFactories = {
  /**
   * Query key factory for collection documents
   * @param collectionId - Collection identifier
   * @param types - Serialized datasource types string
   * @param textFilter - Text filter for search
   * @param statusDirection - Sort direction for status
   * @param updatedDirection - Sort direction for update date
   * @returns Query key array for TanStack Query
   */
  collectionDocuments: (
    collectionId: string,
    types: string,
    textFilter: string,
    statusDirection: 'asc' | 'desc',
    updatedDirection: 'asc' | 'desc'
  ) => ['collectionDocuments', collectionId, types, textFilter, statusDirection, updatedDirection],

  /**
   * Query key factory for modal documents
   * @param collectionId - Collection identifier
   * @param types - Serialized datasource types string
   * @returns Query key array for TanStack Query
   */
  publishDocuments: (collectionId: string) => ['publishDocuments', collectionId],

  /**
   * Query key factory for draft documents count
   * @param collectionId - Collection identifier
   * @returns Query key array for TanStack Query
   */
  draftDocumentsCount: (collectionId: string) => ['draftDocumentsCount', collectionId],

  /**
   * Query key factory for content documents
   * @param collectionId - Collection identifier
   * @param datasourceId - Datasource identifier
   * @param textFilter - Text filter for search
   * @param statusDirection - Sort direction for status
   * @param updatedDirection - Sort direction for update date
   * @returns Query key array for TanStack Query
   */
  content: (
    collectionId: string,
    datasourceId: string,
    textFilter: string,
    statusDirection: 'asc' | 'desc',
    updatedDirection: 'asc' | 'desc'
  ) => ['content', collectionId, datasourceId, textFilter, statusDirection, updatedDirection],

  /**
   * Query key factory for datasources
   * @param collectionId - Collection identifier
   * @param types - Serialized datasource types string
   * @param textFilter - Text filter for search
   * @param statusDirection - Sort direction for status
   * @param updatedDirection - Sort direction for update date
   * @returns Query key array for TanStack Query
   */
  datasources: (
    collectionId: string,
    types: string,
    textFilter: string,
    statusDirection: 'asc' | 'desc',
    updatedDirection: 'asc' | 'desc'
  ) => ['datasources', collectionId, types, textFilter, statusDirection, updatedDirection],

  /**
   * Query key factory for collections
   * @param accountId - Account identifier
   * @param textFilter - Text filter for search
   * @param languageDirection - Sort direction for language
   * @param lastUsedDirection - Sort direction for last used date
   * @returns Query key array for TanStack Query
   */
  collections: (
    accountId: string,
    textFilter: string,
    languageDirection: SortDirection,
    lastUsedDirection: SortDirection
  ) => ['collections', accountId, textFilter, languageDirection, lastUsedDirection],

  /**
   * Query key factory for datasources documents
   * @param collectionId - Collection identifier
   * @param datasourceTab - Datasource tab (faq, documents, webpages, external)
   * @returns Query key array for TanStack Query
   */
  // It used for the client-side table of a collection
  datasourcesDocuments: (collectionId: string, datasourceTab: DatasourceTab) => [
    'datasources-documents',
    collectionId,
    datasourceTab,
  ],

  // Prefix key matching the datasources documents queries of every tab for a collection
  collectionDatasourcesDocuments: (collectionId: string) => ['datasources-documents', collectionId],

  // It used for the contents of a datasource
  // This is used when we are inside of a datasource(folder)
  folderDocuments: (collectionId: string, datasourceId: string) => [
    'folder-documents',
    collectionId,
    datasourceId,
  ],

  // Prefix key matching the folder documents queries of every datasource in a collection
  collectionFolderDocuments: (collectionId: string) => ['folder-documents', collectionId],
} as const;
